import * as THREE from "three";
import { Text, configureTextBuilder } from "troika-three-text";
import { LabelCanvasTexture, getScaledImageDimensions } from "./LabelCanvasTexture.js";
import { getSdfFontUrlForFont } from "./labelFontOptions.js";
import { normalizeRichTextLine } from "./richLabelModel.js";

const WORLD_LABEL_RENDERER_CANVAS = "canvas";
const WORLD_LABEL_RENDERER_SDF = "sdf";
const DEFAULT_WORLD_LABEL_RENDERER = WORLD_LABEL_RENDERER_CANVAS;
const LABEL_SCALE = 200; // Magic Number. Investigate this.
const DEFAULT_SDF_FONT_URL = "/static/data/fonts/neuropol-x.ttf";
const WORLD_LABEL_SDF_GLYPH_SIZE = 128;
const FONT_FILE_URL_PATTERN = /\.(?:ttf|otf|woff)(?:[?#].*)?$/i;
const LEGACY_SCRIPT_TAG_PATTERN = /<\/?(?:sup|sub)>/i;
const SCRIPT_FONT_SCALE = 0.6;
const SUPERSCRIPT_OFFSET_FACTOR = 0.35;
const SUBSCRIPT_OFFSET_FACTOR = -0.25;
// Keep SDF glyphs clearly in front of each face of the backboard so depth-buffer
// precision does not make the nearly-coplanar surfaces fight while the camera moves.
const SDF_BACKGROUND_Z_OFFSET = 0;
const SDF_TEXT_Z_OFFSET = 0.04;
const SDF_BACK_TEXT_ROTATION_Y = Math.PI;
const SDF_BACKGROUND_RENDER_ORDER = 0;
const SDF_TEXT_RENDER_ORDER = 1;
const SDF_IMAGE_RENDER_ORDER = SDF_TEXT_RENDER_ORDER;
const SDF_BACKGROUND_POLYGON_OFFSET_FACTOR = 2;
const SDF_BACKGROUND_POLYGON_OFFSET_UNITS = 2;
const SDF_IMAGE_POSITION_BELOW = "below";
const SDF_IMAGE_POSITION_ABOVE = "above";
const SDF_IMAGE_POSITION_LEFT = "left";
const SDF_IMAGE_POSITION_RIGHT = "right";
const SDF_IMAGE_POSITIONS = [SDF_IMAGE_POSITION_BELOW, SDF_IMAGE_POSITION_ABOVE, SDF_IMAGE_POSITION_LEFT, SDF_IMAGE_POSITION_RIGHT];

let isSdfTextBuilderConfigured = false;

function configureSdfTextBuilder() {
  if (isSdfTextBuilderConfigured) return;

  configureTextBuilder({
    defaultFontURL: DEFAULT_SDF_FONT_URL,
  });
  isSdfTextBuilderConfigured = true;
}

function getRequestedWorldLabelRendererType(content = {}) {
  return content?.style?.textRenderer || content?.textRenderer;
}

function hasRichText(content = {}) {
  return Boolean(content?.richText);
}

function isPlainTextLine(line) {
  return (typeof line === "string" || typeof line === "number") && !LEGACY_SCRIPT_TAG_PATTERN.test(String(line));
}

function isBasicLocalFontText(textArray = []) {
  return textArray.every((line) => /^[\u0009\u000a\u000d\u0020-\u007e]*$/.test(String(line)));
}

function cloneRichTextModel(richTextModel) {
  if (!richTextModel || !Array.isArray(richTextModel.lines)) {
    return undefined;
  }

  return JSON.parse(JSON.stringify(richTextModel));
}

function isFontFileUrl(font) {
  return typeof font === "string" && FONT_FILE_URL_PATTERN.test(font.trim());
}

function hasOwnIndex(array, index) {
  return Object.prototype.hasOwnProperty.call(array, index);
}

function isSupportedRichTextSegment(segment = {}) {
  if (!segment || typeof segment !== "object" || Array.isArray(segment)) return false;
  if (segment.text === undefined || segment.text === null) return false;
  if (segment.script !== undefined && segment.script !== "normal" && segment.script !== "sup" && segment.script !== "sub") return false;
  if (segment.color !== undefined && typeof segment.color !== "string") return false;
  if (segment.fontFamily !== undefined && segment.fontFamily !== null && segment.fontFamily !== "") return false;
  if (segment.fontSize !== undefined && segment.fontSize !== null && segment.fontSize !== "") {
    const fontSize = Number(segment.fontSize);
    if (!Number.isFinite(fontSize) || fontSize <= 0) return false;
  }

  return /^[\u0009\u000a\u000d\u0020-\u007e]*$/.test(String(segment.text));
}

function isSupportedSdfRichTextModel(content = {}) {
  const textArray = Array.isArray(content?.text) ? content.text : [];
  const richText = content?.richText;

  if (!richText || !Array.isArray(richText.lines)) return false;

  return Array.from({ length: Math.max(textArray.length, richText.lines.length) }, (_, index) => {
    if (hasOwnIndex(richText.lines, index)) {
      const line = richText.lines[index];
      return Array.isArray(line) && line.every(isSupportedRichTextSegment);
    }

    return isPlainTextLine(textArray[index]) && isBasicLocalFontText([textArray[index] || ""]);
  }).every(Boolean);
}

function canUseSdfWorldLabelRenderer(content = {}) {
  const style = content?.style || {};
  const textArray = Array.isArray(content?.text) ? content.text : [];

  if (getRequestedWorldLabelRendererType(content) !== WORLD_LABEL_RENDERER_SDF) return false;
  if (style.dropShadow) return false;
  if (hasRichText(content)) return isSupportedSdfRichTextModel(content);
  if (!textArray.every(isPlainTextLine)) return false;

  // The bundled SDF font keeps production rendering local. Fall back to canvas for
  // non-basic glyphs so troika does not need to consult remote unicode fallback fonts.
  if (!isBasicLocalFontText(textArray)) return false;

  return true;
}

function getWorldLabelRendererType(content = {}) {
  const requestedRenderer = getRequestedWorldLabelRendererType(content);

  switch (requestedRenderer) {
    case undefined:
    case null:
    case "":
    case WORLD_LABEL_RENDERER_CANVAS:
      return WORLD_LABEL_RENDERER_CANVAS;
    case WORLD_LABEL_RENDERER_SDF:
      return canUseSdfWorldLabelRenderer(content) ? WORLD_LABEL_RENDERER_SDF : WORLD_LABEL_RENDERER_CANVAS;
    default:
      return DEFAULT_WORLD_LABEL_RENDERER;
  }
}

function getFontSize(style = {}) {
  const explicitFontSize = Number(style.fontSize);
  if (!isNaN(explicitFontSize) && explicitFontSize > 0) {
    return explicitFontSize;
  }

  const fontMatch = String(style.font || "").match(/(\d+(?:\.\d+)?)px/);
  const fontSize = fontMatch ? Number(fontMatch[1]) : NaN;
  return !isNaN(fontSize) && fontSize > 0 ? fontSize : 90;
}

function getNumericStyleValue(value, fallback) {
  const parsedValue = typeof value === "string" ? parseFloat(value) : Number(value);
  return !isNaN(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
}

function getNonNegativeStyleValue(value, fallback) {
  const parsedValue = typeof value === "string" ? parseFloat(value) : Number(value);
  return !isNaN(parsedValue) && parsedValue >= 0 ? parsedValue : fallback;
}

function getTextAlign(style = {}) {
  const textAlign = style.textAlign || style.fontAlign || "center";
  return ["left", "right", "center", "justify"].includes(textAlign) ? textAlign : "center";
}

function getSdfGlyphSize(style = {}, fontSize = getFontSize(style)) {
  const glyphSize = Number(style.sdfGlyphSize);
  if (Number.isFinite(glyphSize) && glyphSize > 0) return glyphSize;

  const normalizedFontSize = getNumericStyleValue(fontSize, getFontSize(style));
  if (normalizedFontSize >= 256) return 512;
  if (normalizedFontSize >= 64) return 256;
  return WORLD_LABEL_SDF_GLYPH_SIZE;
}

function getSdfOutlineWidth(style = {}) {
  const outlineWidth = style.outlineWidth ?? style.sdfOutlineWidth ?? style.strokeWidth ?? style.sdfStrokeWidth;
  if (outlineWidth === undefined || outlineWidth === null || outlineWidth === "") return 0;
  if (typeof outlineWidth === "string" && outlineWidth.trim().endsWith("%")) return outlineWidth.trim();

  const parsedOutlineWidth = Number(outlineWidth);
  return Number.isFinite(parsedOutlineWidth) && parsedOutlineWidth > 0 ? parsedOutlineWidth / LABEL_SCALE : 0;
}

function getSdfOutlineOpacity(style = {}) {
  const outlineOpacity = Number(style.sdfOutlineOpacity ?? style.outlineOpacity ?? style.sdfStrokeOpacity ?? style.strokeOpacity);
  return Number.isFinite(outlineOpacity) ? clamp(outlineOpacity, 0, 1) : 1;
}

function getSdfTextClarityProperties(style = {}, fontSize = getFontSize(style)) {
  const outlineWidth = getSdfOutlineWidth(style);
  return {
    sdfGlyphSize: getSdfGlyphSize(style, fontSize),
    outlineWidth,
    outlineColor: style.sdfOutlineColor || style.outlineColor || style.sdfStrokeColor || style.strokeColor || style.color || "#FFFFFF",
    outlineOpacity: outlineWidth ? getSdfOutlineOpacity(style) : 1,
  };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getSdfPaddingWidth(style = {}) {
  return getNumericStyleValue(style.padding, 40) / LABEL_SCALE;
}

function getSdfPaddingHeight(style = {}) {
  return getNumericStyleValue(style.paddingHeight, 50) / LABEL_SCALE;
}

function getSdfBorderRadius(style = {}, width = 1, height = 1) {
  const radius = getNonNegativeStyleValue(style.borderRadius, 20) / LABEL_SCALE;
  return clamp(radius, 0, Math.max(Math.min(width, height) / 2, 0));
}

function getSdfImagePosition(style = {}) {
  return SDF_IMAGE_POSITIONS.includes(style.imagePosition) ? style.imagePosition : SDF_IMAGE_POSITION_BELOW;
}

function createRoundedRectGeometry(width, height, radius) {
  if (!radius) {
    return new THREE.PlaneGeometry(width, height);
  }

  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const clampedRadius = clamp(radius, 0, Math.min(halfWidth, halfHeight));
  const shape = new THREE.Shape();

  shape.moveTo(-halfWidth + clampedRadius, halfHeight);
  shape.lineTo(halfWidth - clampedRadius, halfHeight);
  shape.quadraticCurveTo(halfWidth, halfHeight, halfWidth, halfHeight - clampedRadius);
  shape.lineTo(halfWidth, -halfHeight + clampedRadius);
  shape.quadraticCurveTo(halfWidth, -halfHeight, halfWidth - clampedRadius, -halfHeight);
  shape.lineTo(-halfWidth + clampedRadius, -halfHeight);
  shape.quadraticCurveTo(-halfWidth, -halfHeight, -halfWidth, -halfHeight + clampedRadius);
  shape.lineTo(-halfWidth, halfHeight - clampedRadius);
  shape.quadraticCurveTo(-halfWidth, halfHeight, -halfWidth + clampedRadius, halfHeight);
  if (shape.closePath) {
    shape.closePath();
  }

  return new THREE.ShapeGeometry(shape);
}

function setMaterialDepthTest(material, depthTest) {
  if (Array.isArray(material)) {
    material.forEach((childMaterial) => setMaterialDepthTest(childMaterial, depthTest));
    return;
  }

  if (material) {
    material.depthTest = depthTest;
    material.needsUpdate = true;
  }
}

function configureSdfTextMaterial(material) {
  if (Array.isArray(material)) {
    material.forEach(configureSdfTextMaterial);
    return;
  }

  if (material) {
    material.depthWrite = false;
    material.side = THREE.FrontSide;
    material.needsUpdate = true;
  }
}

function applyMaterialColorAndOpacity(material, colorStyle) {
  if (!material) return;

  const color = new THREE.Color();
  const colorValue = colorStyle || "#333333";
  const rgbaMatch = typeof colorValue === "string" ? colorValue.match(/rgba?\(([^)]+)\)/i) : null;

  if (rgbaMatch) {
    const values = rgbaMatch[1].split(",").map((value) => parseFloat(value.trim()));
    color.setRGB((values[0] || 0) / 255, (values[1] || 0) / 255, (values[2] || 0) / 255);
    material.color.copy(color);
    material.opacity = values.length === 4 && !isNaN(values[3]) ? clamp(values[3], 0, 1) : 1;
    material.transparent = material.opacity < 1;
  } else {
    material.color.setStyle(colorValue);
    material.opacity = colorValue === "transparent" ? 0 : 1;
    material.transparent = material.opacity < 1;
  }

  material.needsUpdate = true;
}

class CanvasWorldLabelRenderer {
  constructor({ content, style }) {
    this.type = WORLD_LABEL_RENDERER_CANVAS;
    this.requestedType = getRequestedWorldLabelRendererType(content);
    this.canvasManager = new LabelCanvasTexture(content.text, style, content.richText);
    this.canvasTexture = this.canvasManager;

    this.texture = new THREE.Texture(this.canvasManager.getCanvas());
    this.texture.needsUpdate = true;
    this.texture.minFilter = THREE.LinearFilter;

    const material = new THREE.MeshBasicMaterial({ map: this.texture, transparent: true, side: THREE.DoubleSide });
    const geometry = new THREE.PlaneGeometry(1, 1);
    this.mesh = new THREE.Mesh(geometry, material);
  }

  getCanvasManager() {
    return this.canvasManager;
  }

  getCanvas() {
    return this.canvasManager.getCanvas();
  }

  getMesh() {
    return this.mesh;
  }

  getTexture() {
    return this.texture;
  }

  get textArray() {
    return this.canvasManager.textArray;
  }

  get style() {
    return this.canvasManager.style;
  }

  setDepthTest(depthTest) {
    setMaterialDepthTest(this.mesh.material, depthTest);
  }

  render() {
    return this.canvasManager.render().then(() => {
      this.updateMeshFromCanvas();
      return this;
    });
  }

  updateMeshFromCanvas() {
    const canvas = this.getCanvas();
    const width = canvas.width / LABEL_SCALE;
    const height = canvas.height / LABEL_SCALE;

    this.mesh.scale.set(width, height, 1);
    this.mesh.updateMatrixWorld();
    this.mesh.material.needsUpdate = true;
    this.texture.needsUpdate = true;
  }

  replaceText(textArray, richTextModel) {
    this.canvasManager.replaceText(textArray, richTextModel);
  }

  addText(textLine) {
    this.canvasManager.addText(textLine);
  }

  editText(textID, textLine) {
    this.canvasManager.editText(textID, textLine);
  }

  setStyle(style) {
    this.canvasManager.setStyle(style);
  }
}

function disposeMaterial(material) {
  if (Array.isArray(material)) {
    material.forEach(disposeMaterial);
    return;
  }

  if (material?.dispose) {
    material.dispose();
  }
}

function disposeTexture(texture) {
  if (texture?.dispose) {
    texture.dispose();
  }
}

function disposeMeshGeometry(mesh) {
  if (mesh?.geometry?.dispose) {
    mesh.geometry.dispose();
  }
}

function disposeTextMesh(textMesh) {
  if (!textMesh) return;

  if (textMesh.geometry?.dispose) {
    textMesh.geometry.dispose();
  }
  disposeMaterial(textMesh.material);
  if (textMesh.dispose) {
    textMesh.dispose();
  }
}

class SdfWorldLabelRenderer {
  constructor({ content, style }) {
    configureSdfTextBuilder();

    this.type = WORLD_LABEL_RENDERER_SDF;
    this.requestedType = getRequestedWorldLabelRendererType(content);
    this.textArray = Array.isArray(content.text) ? [...content.text] : [];
    this.richText = cloneRichTextModel(content.richText);
    this.richTextMeshes = [];
    this.richTextLineLayouts = [];
    this.imageUrl = undefined;
    this.imageTexture = null;
    this.imageLoadPromise = null;
    this.imageLoadToken = 0;
    this.imageDimensions = { width: 0, height: 0 };
    this.imageError = null;
    this.depthTest = undefined;
    this.style = Object.assign(
      {
        backgroundColor: "#333333",
        color: "#FFFFFF",
        padding: 40,
        paddingHeight: 50,
        font: "90px Sans-Serif, Arial",
        fontSize: 90,
        textAlign: "center",
        lineSpacing: 20,
      },
      style
    );

    this.containerMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.containerMaterial);

    this.backgroundMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      transparent: true,
      polygonOffset: true,
      polygonOffsetFactor: SDF_BACKGROUND_POLYGON_OFFSET_FACTOR,
      polygonOffsetUnits: SDF_BACKGROUND_POLYGON_OFFSET_UNITS,
      depthWrite: true,
    });
    this.backgroundMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.backgroundMaterial);
    this.backgroundMesh.position.z = SDF_BACKGROUND_Z_OFFSET;
    this.backgroundMesh.renderOrder = SDF_BACKGROUND_RENDER_ORDER;
    this.mesh.add(this.backgroundMesh);

    this.frontTextGroup = new THREE.Group();
    this.frontTextGroup.position.z = SDF_TEXT_Z_OFFSET;
    this.frontTextGroup.renderOrder = SDF_TEXT_RENDER_ORDER;
    this.mesh.add(this.frontTextGroup);

    this.backTextGroup = new THREE.Group();
    this.backTextGroup.position.z = -SDF_TEXT_Z_OFFSET;
    this.backTextGroup.rotation.y = SDF_BACK_TEXT_ROTATION_Y;
    this.backTextGroup.renderOrder = SDF_TEXT_RENDER_ORDER;
    this.mesh.add(this.backTextGroup);

    this.textMaterial = this.createTextMaterial();
    this.backTextMaterial = this.createTextMaterial();
    this.textMesh = this.createTextMesh(this.textMaterial);
    this.backTextMesh = this.createTextMesh(this.backTextMaterial);
    this.pendingTextSyncs = new WeakMap();
    this.frontTextGroup.add(this.textMesh);
    this.backTextGroup.add(this.backTextMesh);

    this.imageMaterial = this.createImageMaterial();
    this.backImageMaterial = this.createImageMaterial();
    this.imageMesh = this.createImageMesh(this.imageMaterial);
    this.backImageMesh = this.createImageMesh(this.backImageMaterial);
    this.frontTextGroup.add(this.imageMesh);
    this.backTextGroup.add(this.backImageMesh);

    this.applyStyleToMeshes();
  }

  getCanvasManager() {
    return null;
  }

  getCanvas() {
    return null;
  }

  getMesh() {
    return this.mesh;
  }

  getTexture() {
    return null;
  }

  setDepthTest(depthTest) {
    this.depthTest = depthTest;
    setMaterialDepthTest(this.containerMaterial, depthTest);
    setMaterialDepthTest(this.backgroundMaterial, depthTest);
    setMaterialDepthTest(this.textMesh.material, depthTest);
    setMaterialDepthTest(this.backTextMesh.material, depthTest);
    setMaterialDepthTest(this.imageMesh.material, depthTest);
    setMaterialDepthTest(this.backImageMesh.material, depthTest);
    this.richTextMeshes.forEach((run) => {
      setMaterialDepthTest(run.mesh.material, depthTest);
      setMaterialDepthTest(run.backMesh.material, depthTest);
    });
  }

  createTextMaterial() {
    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      side: THREE.FrontSide,
      depthWrite: false,
    });
    configureSdfTextMaterial(material);
    if (this.depthTest !== undefined) {
      setMaterialDepthTest(material, this.depthTest);
    }
    return material;
  }

  createTextMesh(material) {
    const textMesh = new Text();
    textMesh.material = material || this.createTextMaterial();
    textMesh.position.z = 0;
    textMesh.renderOrder = SDF_TEXT_RENDER_ORDER;
    textMesh.sdfGlyphSize = getSdfGlyphSize(this.style);
    configureSdfTextMaterial(textMesh.material);
    if (this.depthTest !== undefined) {
      setMaterialDepthTest(textMesh.material, this.depthTest);
    }
    return textMesh;
  }

  createImageMaterial() {
    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      side: THREE.FrontSide,
      depthWrite: false,
    });
    if (this.depthTest !== undefined) {
      setMaterialDepthTest(material, this.depthTest);
    }
    return material;
  }

  createImageMesh(material) {
    const imageMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material || this.createImageMaterial());
    imageMesh.position.z = 0;
    imageMesh.renderOrder = SDF_IMAGE_RENDER_ORDER;
    imageMesh.visible = false;
    if (this.depthTest !== undefined) {
      setMaterialDepthTest(imageMesh.material, this.depthTest);
    }
    return imageMesh;
  }

  setTextMeshProperty(textMesh, property, value) {
    if (textMesh[property] === value) {
      return false;
    }

    textMesh[property] = value;
    return true;
  }

  hasRichTextLayout() {
    return Boolean(this.richText && Array.isArray(this.richText.lines));
  }

  getRenderableTextLines() {
    return Array.isArray(this.textArray) ? this.textArray.map((line) => String(line ?? "")) : [];
  }

  getRichRenderableLines() {
    const textLines = this.getRenderableTextLines();
    const richLines = Array.isArray(this.richText?.lines) ? this.richText.lines : [];
    const lineCount = Math.max(textLines.length, richLines.length);

    return Array.from({ length: lineCount }, (_, index) => {
      if (hasOwnIndex(richLines, index)) {
        return normalizeRichTextLine(richLines[index]);
      }

      return [{ text: String(textLines[index] ?? ""), script: "normal" }];
    });
  }

  getTextValue() {
    return this.getRenderableTextLines().join("\n");
  }

  hasPlainTextLayout() {
    return this.getRenderableTextLines().length > 0;
  }

  getBaseFontSize() {
    return getFontSize(this.style);
  }

  getSdfFontUrl() {
    return this.style.sdfFontUrl
      || this.style.fontUrl
      || (isFontFileUrl(this.style.font) ? this.style.font : undefined)
      || getSdfFontUrlForFont(this.style.font)
      || DEFAULT_SDF_FONT_URL;
  }

  getSegmentStyleFontSize(segment = {}) {
    const baseFontSize = this.getBaseFontSize();
    const segmentFontSize = Number(segment.fontSize);
    return Number.isFinite(segmentFontSize) && segmentFontSize > 0 ? segmentFontSize : baseFontSize;
  }

  getSegmentFontSize(segment = {}) {
    const fontSize = this.getSegmentStyleFontSize(segment);

    return (segment.script === "sup" || segment.script === "sub") ? fontSize * SCRIPT_FONT_SCALE : fontSize;
  }

  getSegmentYOffset(segment = {}) {
    const baseFontSize = this.getBaseFontSize();

    if (segment.script === "sup") {
      return (baseFontSize * SUPERSCRIPT_OFFSET_FACTOR) / LABEL_SCALE;
    }

    if (segment.script === "sub") {
      return (baseFontSize * SUBSCRIPT_OFFSET_FACTOR) / LABEL_SCALE;
    }

    return 0;
  }

  getRichLineMetrics(line = []) {
    const baseFontSize = this.getBaseFontSize() / LABEL_SCALE;
    let ascent = baseFontSize / 2;
    let descent = baseFontSize / 2;

    line.forEach((segment) => {
      const segmentFontSize = this.getSegmentFontSize(segment) / LABEL_SCALE;
      const segmentOffset = this.getSegmentYOffset(segment);
      ascent = Math.max(ascent, segmentFontSize / 2 + segmentOffset);
      descent = Math.max(descent, segmentFontSize / 2 - segmentOffset);
    });

    return {
      ascent,
      descent,
      height: ascent + descent,
    };
  }

  getRichTextLayout() {
    const lines = this.getRichRenderableLines();
    const lineSpacing = getNumericStyleValue(this.style.lineSpacing, 20) / LABEL_SCALE;
    const metrics = lines.map((line) => this.getRichLineMetrics(line));
    const totalHeight = metrics.reduce((sum, metric, index) => sum + metric.height + (index > 0 ? lineSpacing : 0), 0);
    let cursorTop = totalHeight / 2;

    const lineYPositions = metrics.map((metric, index) => {
      if (index > 0) {
        cursorTop -= lineSpacing;
      }

      const y = cursorTop - metric.ascent;
      cursorTop = y - metric.descent;
      return y;
    });

    return {
      lines,
      metrics,
      lineSpacing,
      totalHeight,
      lineYPositions,
    };
  }

  applyPlainTextStyle(textMesh) {
    const fontSize = this.getBaseFontSize();
    const textAlign = getTextAlign(this.style);
    let changed = false;

    changed = this.setTextMeshProperty(textMesh, "text", this.getTextValue()) || changed;
    changed = this.setTextMeshProperty(textMesh, "font", this.getSdfFontUrl()) || changed;
    changed = this.setTextMeshProperty(textMesh, "fontSize", fontSize / LABEL_SCALE) || changed;
    changed = this.setTextMeshProperty(textMesh, "color", this.style.color || "#FFFFFF") || changed;
    changed = this.setTextMeshProperty(textMesh, "textAlign", textAlign) || changed;
    changed = this.setTextMeshProperty(textMesh, "anchorX", "center") || changed;
    changed = this.setTextMeshProperty(textMesh, "anchorY", "middle") || changed;
    changed = this.setTextMeshProperty(textMesh, "whiteSpace", "nowrap") || changed;
    changed = this.setTextMeshProperty(textMesh, "lineHeight", (fontSize + getNumericStyleValue(this.style.lineSpacing, 20)) / fontSize) || changed;
    Object.entries(getSdfTextClarityProperties(this.style, fontSize)).forEach(([property, value]) => {
      changed = this.setTextMeshProperty(textMesh, property, value) || changed;
    });

    if (this.style.fontWeight) {
      changed = this.setTextMeshProperty(textMesh, "fontWeight", this.style.fontWeight) || changed;
    }
    if (this.style.fontStyle) {
      changed = this.setTextMeshProperty(textMesh, "fontStyle", this.style.fontStyle) || changed;
    }
    if (this.style.letterSpacing !== undefined) {
      changed = this.setTextMeshProperty(textMesh, "letterSpacing", Number(this.style.letterSpacing) / LABEL_SCALE) || changed;
    }

    return changed;
  }

  applyStyleToMeshes() {
    applyMaterialColorAndOpacity(this.backgroundMaterial, this.style.backgroundColor);
    return [
      { textMesh: this.textMesh, needsSync: this.applyPlainTextStyle(this.textMesh) },
      { textMesh: this.backTextMesh, needsSync: this.applyPlainTextStyle(this.backTextMesh) },
    ];
  }

  getImageWorldDimensions() {
    const image = this.imageTexture?.image;
    if (!image) {
      this.imageDimensions = { width: 0, height: 0 };
      return this.imageDimensions;
    }

    const imageDimensions = getScaledImageDimensions(image, this.style);
    this.imageDimensions = {
      width: imageDimensions.width / LABEL_SCALE,
      height: imageDimensions.height / LABEL_SCALE,
    };
    return this.imageDimensions;
  }

  setImageMeshesVisible(visible) {
    this.imageMesh.visible = visible;
    this.backImageMesh.visible = visible;
  }

  clearImageTexture({ clearUrl = true, clearError = true } = {}) {
    this.imageLoadToken += 1;
    if (clearUrl) {
      this.imageUrl = undefined;
    }
    if (clearError) {
      this.imageError = null;
    }
    if (this.imageTexture) {
      disposeTexture(this.imageTexture);
    }
    this.imageTexture = null;
    this.imageLoadPromise = null;
    this.imageDimensions = { width: 0, height: 0 };
    this.imageMaterial.map = null;
    this.backImageMaterial.map = null;
    this.imageMaterial.needsUpdate = true;
    this.backImageMaterial.needsUpdate = true;
    this.setImageMeshesVisible(false);
  }

  updateImageMeshGeometry(width, height) {
    disposeMeshGeometry(this.imageMesh);
    disposeMeshGeometry(this.backImageMesh);
    this.imageMesh.geometry = new THREE.PlaneGeometry(width || 1, height || 1);
    this.backImageMesh.geometry = new THREE.PlaneGeometry(width || 1, height || 1);
    this.imageMesh.renderOrder = SDF_IMAGE_RENDER_ORDER;
    this.backImageMesh.renderOrder = SDF_IMAGE_RENDER_ORDER;
  }

  applyImageTexture(texture) {
    this.imageTexture = texture;
    this.imageTexture.minFilter = THREE.LinearFilter;
    this.imageTexture.needsUpdate = true;
    this.imageMaterial.map = texture;
    this.backImageMaterial.map = texture;
    this.imageMaterial.needsUpdate = true;
    this.backImageMaterial.needsUpdate = true;

    const dimensions = this.getImageWorldDimensions();
    this.updateImageMeshGeometry(dimensions.width, dimensions.height);
    this.setImageMeshesVisible(dimensions.width > 0 && dimensions.height > 0);
  }

  loadImageTexture(imageUrl) {
    if (!imageUrl) {
      this.clearImageTexture();
      return Promise.resolve(null);
    }

    if (this.imageUrl === imageUrl && this.imageTexture) {
      this.applyImageTexture(this.imageTexture);
      return Promise.resolve(this.imageTexture);
    }

    if (this.imageUrl === imageUrl && this.imageLoadPromise) {
      return this.imageLoadPromise;
    }

    this.clearImageTexture({ clearUrl: false });
    this.imageUrl = imageUrl;
    this.imageError = null;
    const loadToken = this.imageLoadToken;

    this.imageLoadPromise = new Promise((resolve) => {
      const loader = new THREE.TextureLoader();
      if (loader.setCrossOrigin) {
        loader.setCrossOrigin("Anonymous");
      }

      const handleLoadError = (error) => {
        if (loadToken === this.imageLoadToken && this.imageUrl === imageUrl) {
          this.imageError = error || true;
          this.imageLoadPromise = null;
          this.imageTexture = null;
          this.imageDimensions = { width: 0, height: 0 };
          this.imageMaterial.map = null;
          this.backImageMaterial.map = null;
          this.imageMaterial.needsUpdate = true;
          this.backImageMaterial.needsUpdate = true;
          this.setImageMeshesVisible(false);
        }
        resolve(null);
      };

      try {
        loader.load(
          imageUrl,
          (texture) => {
            if (loadToken !== this.imageLoadToken || this.imageUrl !== imageUrl) {
              disposeTexture(texture);
              resolve(null);
              return;
            }

            this.imageLoadPromise = null;
            this.applyImageTexture(texture);
            resolve(texture);
          },
          undefined,
          handleLoadError
        );
      } catch (error) {
        handleLoadError(error);
      }
    });

    return this.imageLoadPromise;
  }

  prepareImageTexture() {
    const imageUrl = this.style.image;
    if (!imageUrl) {
      this.clearImageTexture();
      return Promise.resolve(null);
    }

    return this.loadImageTexture(imageUrl);
  }

  getLayoutWithImage(textWidth, textHeight, paddingWidth, paddingHeight, extraBuffer) {
    const imageDimensions = this.getImageWorldDimensions();
    const hasImage = imageDimensions.width > 0 && imageDimensions.height > 0;
    const imagePosition = getSdfImagePosition(this.style);

    if (!hasImage) {
      return {
        hasImage: false,
        imageDimensions,
        imagePosition,
        width: textWidth + paddingWidth + extraBuffer,
        height: textHeight + paddingHeight + extraBuffer,
        textCenter: { x: 0, y: 0 },
        imageCenter: { x: 0, y: 0 },
      };
    }

    if (imagePosition === SDF_IMAGE_POSITION_LEFT || imagePosition === SDF_IMAGE_POSITION_RIGHT) {
      const contentWidth = textWidth + imageDimensions.width;
      const width = contentWidth + paddingWidth + extraBuffer;
      const height = Math.max(textHeight, imageDimensions.height) + paddingHeight + extraBuffer;
      const imageX = imagePosition === SDF_IMAGE_POSITION_LEFT ? -textWidth / 2 : textWidth / 2;
      const textX = imagePosition === SDF_IMAGE_POSITION_LEFT ? imageDimensions.width / 2 : -imageDimensions.width / 2;

      return {
        hasImage,
        imageDimensions,
        imagePosition,
        width,
        height,
        textCenter: { x: textX, y: 0 },
        imageCenter: { x: imageX, y: 0 },
      };
    }

    const width = Math.max(textWidth, imageDimensions.width) + paddingWidth + extraBuffer;
    const height = textHeight + imageDimensions.height + paddingHeight + extraBuffer;
    const imageY = imagePosition === SDF_IMAGE_POSITION_ABOVE ? textHeight / 2 : -textHeight / 2;
    const textY = imagePosition === SDF_IMAGE_POSITION_ABOVE ? -imageDimensions.height / 2 : imageDimensions.height / 2;

    return {
      hasImage,
      imageDimensions,
      imagePosition,
      width,
      height,
      textCenter: { x: 0, y: textY },
      imageCenter: { x: 0, y: imageY },
    };
  }

  positionImageMeshes(layout) {
    if (!layout?.hasImage) {
      this.setImageMeshesVisible(false);
      return;
    }

    const { width, height } = layout.imageDimensions;
    this.updateImageMeshGeometry(width, height);
    this.imageMesh.position.x = layout.imageCenter.x;
    this.imageMesh.position.y = layout.imageCenter.y;
    this.imageMesh.position.z = 0;
    this.backImageMesh.position.x = this.imageMesh.position.x;
    this.backImageMesh.position.y = this.imageMesh.position.y;
    this.backImageMesh.position.z = 0;
    this.setImageMeshesVisible(true);
  }

  render() {
    applyMaterialColorAndOpacity(this.backgroundMaterial, this.style.backgroundColor);

    if (this.hasRichTextLayout()) {
      return this.renderRichText();
    }

    return this.renderPlainText();
  }

  renderPlainText() {
    this.clearRichTextMeshes();
    this.textMesh.visible = true;
    this.backTextMesh.visible = true;
    const plainTextMeshes = this.applyStyleToMeshes();
    const imagePromise = this.prepareImageTexture();
    const textPromise = plainTextMeshes.every(({ textMesh, needsSync }) => textMesh.textRenderInfo && !needsSync && !this.pendingTextSyncs.has(textMesh))
      ? Promise.resolve()
      : this.syncTextMeshes(plainTextMeshes);

    return Promise.all([textPromise, imagePromise]).then(() => {
      this.updateMeshFromText();
      return this;
    });
  }

  createRichTextSegmentMesh(segment) {
    const textMesh = this.createTextMesh();
    const segmentStyleFontSize = this.getSegmentStyleFontSize(segment);
    textMesh.text = String(segment.text ?? "");
    textMesh.font = this.getSdfFontUrl();
    textMesh.fontSize = this.getSegmentFontSize(segment) / LABEL_SCALE;
    textMesh.color = segment.color || this.style.color || "#FFFFFF";
    textMesh.textAlign = "left";
    textMesh.anchorX = "left";
    textMesh.anchorY = "middle";
    textMesh.whiteSpace = "nowrap";
    textMesh.lineHeight = 1;
    textMesh.position.z = 0;
    Object.assign(textMesh, getSdfTextClarityProperties(this.style, segmentStyleFontSize));

    if (this.style.fontWeight) {
      textMesh.fontWeight = this.style.fontWeight;
    }
    if (this.style.fontStyle) {
      textMesh.fontStyle = this.style.fontStyle;
    }
    if (this.style.letterSpacing !== undefined) {
      textMesh.letterSpacing = Number(this.style.letterSpacing) / LABEL_SCALE;
    }

    return textMesh;
  }

  renderRichText() {
    this.textMesh.visible = false;
    this.backTextMesh.visible = false;
    this.clearRichTextMeshes();

    const imagePromise = this.prepareImageTexture();
    const layout = this.getRichTextLayout();
    this.richTextLineLayouts = layout.lines.map((line, lineIndex) => ({
      lineIndex,
      y: layout.lineYPositions[lineIndex],
      metric: layout.metrics[lineIndex],
      runs: [],
    }));

    layout.lines.forEach((line, lineIndex) => {
      line.forEach((segment) => {
        const textMesh = this.createRichTextSegmentMesh(segment);
        const backTextMesh = this.createRichTextSegmentMesh(segment);

        const run = {
          lineIndex,
          segment,
          mesh: textMesh,
          backMesh: backTextMesh,
          width: 0,
          bounds: [0, -textMesh.fontSize / 2, 0, textMesh.fontSize / 2],
        };
        this.richTextMeshes.push(run);
        this.richTextLineLayouts[lineIndex].runs.push(run);
        this.frontTextGroup.add(textMesh);
        this.backTextGroup.add(backTextMesh);
      });
    });

    const textPromise = this.syncTextMeshes(this.richTextMeshes.flatMap((run) => [
      { textMesh: run.mesh, needsSync: true },
      { textMesh: run.backMesh, needsSync: true },
    ]));

    return Promise.all([textPromise, imagePromise]).then(() => {
      this.updateMeshFromRichText(layout);
      return this;
    });
  }

  syncTextMeshes(textMeshes = []) {
    if (textMeshes.length === 0) {
      return Promise.resolve();
    }

    return Promise.all(textMeshes.map((entry) => this.syncTextMesh(entry)));
  }

  syncTextMesh(entry) {
    const textMesh = entry?.textMesh || entry?.mesh || entry;
    const needsSync = Boolean(entry?.needsSync);
    const pendingSync = this.pendingTextSyncs.get(textMesh);

    if (!needsSync && pendingSync) {
      return pendingSync;
    }

    if (!needsSync && textMesh.textRenderInfo) {
      return Promise.resolve();
    }

    const syncPromise = new Promise((resolve) => {
      textMesh.sync(resolve);
    });
    const trackedPromise = syncPromise.then(() => {
      if (this.pendingTextSyncs.get(textMesh) === trackedPromise) {
        this.pendingTextSyncs.delete(textMesh);
      }
    });
    this.pendingTextSyncs.set(textMesh, trackedPromise);

    return trackedPromise;
  }

  getRunBounds(run) {
    const fallbackHeight = Math.max(run.mesh.fontSize || this.getBaseFontSize() / LABEL_SCALE, 0.1);
    const bounds = run.mesh.textRenderInfo?.blockBounds || [0, -fallbackHeight / 2, 0, fallbackHeight / 2];
    const width = Math.max(bounds[2] - bounds[0], 0);

    return {
      bounds,
      width,
    };
  }

  updateBackboardGeometry(width, height) {
    const borderRadius = getSdfBorderRadius(this.style, width, height);

    if (this.mesh.geometry?.dispose) {
      this.mesh.geometry.dispose();
    }
    this.mesh.geometry = new THREE.PlaneGeometry(width, height);
    this.mesh.scale.set(1, 1, 1);

    if (this.backgroundMesh.geometry?.dispose) {
      this.backgroundMesh.geometry.dispose();
    }
    this.backgroundMesh.geometry = createRoundedRectGeometry(width, height, borderRadius);
  }

  updateMeshFromText() {
    const bounds = this.textMesh.textRenderInfo?.blockBounds || [-0.5, -0.25, 0.5, 0.25];
    const hasTextLayout = this.hasPlainTextLayout();
    const textWidth = hasTextLayout ? Math.max(bounds[2] - bounds[0], 0.1) : 0;
    const textHeight = hasTextLayout ? Math.max(bounds[3] - bounds[1], 0.1) : 0;
    const fontSize = this.getBaseFontSize();
    const paddingWidth = getSdfPaddingWidth(this.style);
    const paddingHeight = getSdfPaddingHeight(this.style);
    const extraBuffer = (fontSize * 0.2) / LABEL_SCALE;
    const layout = this.getLayoutWithImage(textWidth, textHeight, paddingWidth, paddingHeight, extraBuffer);
    const width = layout.width;
    const height = layout.height;

    this.updateBackboardGeometry(width, height);
    this.textMesh.visible = hasTextLayout;
    this.backTextMesh.visible = hasTextLayout;

    const textAlign = getTextAlign(this.style);
    if (layout.hasImage) {
      switch (textAlign) {
        case "left":
        case "justify":
          this.textMesh.position.x = layout.textCenter.x - textWidth / 2 - bounds[0];
          break;
        case "right":
          this.textMesh.position.x = layout.textCenter.x + textWidth / 2 - bounds[2];
          break;
        case "center":
        default:
          this.textMesh.position.x = layout.textCenter.x - ((bounds[0] + bounds[2]) / 2);
          break;
      }
      this.textMesh.position.y = layout.textCenter.y - ((bounds[1] + bounds[3]) / 2);
      this.positionImageMeshes(layout);
    } else {
      this.setImageMeshesVisible(false);
      switch (textAlign) {
        case "left":
        case "justify":
          this.textMesh.position.x = -width / 2 + paddingWidth / 2 - bounds[0];
          break;
        case "right":
          this.textMesh.position.x = width / 2 - paddingWidth / 2 - bounds[2];
          break;
        case "center":
        default:
          this.textMesh.position.x = -((bounds[0] + bounds[2]) / 2);
          break;
      }
      this.textMesh.position.y = -((bounds[1] + bounds[3]) / 2);
    }

    this.textMesh.position.z = 0;
    this.textMesh.renderOrder = SDF_TEXT_RENDER_ORDER;
    this.backTextMesh.position.x = this.textMesh.position.x;
    this.backTextMesh.position.y = this.textMesh.position.y;
    this.backTextMesh.position.z = 0;
    this.backTextMesh.renderOrder = SDF_TEXT_RENDER_ORDER;

    this.mesh.updateMatrixWorld();
    this.backgroundMaterial.needsUpdate = true;
    configureSdfTextMaterial(this.textMesh.material);
    configureSdfTextMaterial(this.backTextMesh.material);
    setMaterialDepthTest(this.textMesh.material, this.backgroundMaterial.depthTest);
    setMaterialDepthTest(this.backTextMesh.material, this.backgroundMaterial.depthTest);
    setMaterialDepthTest(this.imageMesh.material, this.backgroundMaterial.depthTest);
    setMaterialDepthTest(this.backImageMesh.material, this.backgroundMaterial.depthTest);
  }

  updateMeshFromRichText(layout) {
    let maxLineWidth = 0;

    this.richTextMeshes.forEach((run) => {
      const { bounds, width } = this.getRunBounds(run);
      run.bounds = bounds;
      run.width = width;
    });

    this.richTextLineLayouts.forEach((lineLayout) => {
      lineLayout.width = lineLayout.runs.reduce((sum, run) => sum + run.width, 0);
      maxLineWidth = Math.max(maxLineWidth, lineLayout.width);
    });

    const fontSize = this.getBaseFontSize();
    const paddingWidth = getSdfPaddingWidth(this.style);
    const paddingHeight = getSdfPaddingHeight(this.style);
    const extraBuffer = (fontSize * 0.2) / LABEL_SCALE;
    const hasTextLayout = layout.lines.length > 0;
    const textHeight = hasTextLayout ? Math.max(layout.totalHeight, 0.1) : 0;
    const textWidth = hasTextLayout ? Math.max(maxLineWidth, 0.1) : 0;
    const layoutWithImage = this.getLayoutWithImage(textWidth, textHeight, paddingWidth, paddingHeight, extraBuffer);
    const width = layoutWithImage.width;
    const height = layoutWithImage.height;
    const textAlign = getTextAlign(this.style);

    this.updateBackboardGeometry(width, height);

    this.richTextLineLayouts.forEach((lineLayout) => {
      let lineStartX;
      if (layoutWithImage.hasImage) {
        switch (textAlign) {
          case "left":
          case "justify":
            lineStartX = layoutWithImage.textCenter.x - textWidth / 2;
            break;
          case "right":
            lineStartX = layoutWithImage.textCenter.x + textWidth / 2 - lineLayout.width;
            break;
          case "center":
          default:
            lineStartX = layoutWithImage.textCenter.x - lineLayout.width / 2;
            break;
        }
      } else {
        switch (textAlign) {
          case "left":
          case "justify":
            lineStartX = -width / 2 + paddingWidth / 2;
            break;
          case "right":
            lineStartX = width / 2 - paddingWidth / 2 - lineLayout.width;
            break;
          case "center":
          default:
            lineStartX = -lineLayout.width / 2;
            break;
        }
      }

      let cursorX = 0;
      lineLayout.runs.forEach((run) => {
        const bounds = run.bounds || [0, -0.25, run.width || 0, 0.25];
        run.mesh.position.x = lineStartX + cursorX - bounds[0];
        run.mesh.position.y = layoutWithImage.textCenter.y + lineLayout.y + this.getSegmentYOffset(run.segment) - ((bounds[1] + bounds[3]) / 2);
        run.mesh.position.z = 0;
        run.mesh.renderOrder = SDF_TEXT_RENDER_ORDER;
        run.backMesh.position.x = run.mesh.position.x;
        run.backMesh.position.y = run.mesh.position.y;
        run.backMesh.position.z = 0;
        run.backMesh.renderOrder = SDF_TEXT_RENDER_ORDER;
        cursorX += run.width;
      });
    });

    this.positionImageMeshes(layoutWithImage);

    this.mesh.updateMatrixWorld();
    this.backgroundMaterial.needsUpdate = true;
    this.richTextMeshes.forEach((run) => {
      configureSdfTextMaterial(run.mesh.material);
      configureSdfTextMaterial(run.backMesh.material);
      setMaterialDepthTest(run.mesh.material, this.backgroundMaterial.depthTest);
      setMaterialDepthTest(run.backMesh.material, this.backgroundMaterial.depthTest);
    });
    setMaterialDepthTest(this.imageMesh.material, this.backgroundMaterial.depthTest);
    setMaterialDepthTest(this.backImageMesh.material, this.backgroundMaterial.depthTest);
  }

  replaceText(textArray, richTextModel) {
    this.textArray = Array.isArray(textArray) ? [...textArray] : [];
    this.richText = cloneRichTextModel(richTextModel);
  }

  addText(textLine) {
    this.richText = undefined;
    this.textArray.push(textLine);
  }

  editText(textID, textLine) {
    this.richText = undefined;
    if (this.textArray[textID] !== undefined) {
      this.textArray[textID] = textLine;
    }
  }

  setStyle(style) {
    const nextStyle = Object.assign({}, style);
    if (
      nextStyle &&
      Object.prototype.hasOwnProperty.call(nextStyle, "font") &&
      !Object.prototype.hasOwnProperty.call(nextStyle, "fontSize")
    ) {
      const fontMatch = String(nextStyle.font || "").match(/(\d+(?:\.\d+)?)px/);
      if (fontMatch) {
        nextStyle.fontSize = Number(fontMatch[1]);
      }
    }
    this.style = Object.assign({}, this.style, nextStyle);
  }

  clearRichTextMeshes() {
    this.richTextMeshes.forEach((run) => {
      if (this.frontTextGroup.remove) {
        this.frontTextGroup.remove(run.mesh);
      }
      if (this.backTextGroup.remove) {
        this.backTextGroup.remove(run.backMesh);
      }
      disposeTextMesh(run.mesh);
      disposeTextMesh(run.backMesh);
    });
    this.richTextMeshes = [];
    this.richTextLineLayouts = [];
  }

  dispose() {
    this.clearRichTextMeshes();
    this.clearImageTexture();
    disposeTextMesh(this.textMesh);
    disposeTextMesh(this.backTextMesh);
    disposeMeshGeometry(this.imageMesh);
    disposeMeshGeometry(this.backImageMesh);
    disposeMaterial(this.imageMaterial);
    disposeMaterial(this.backImageMaterial);
    if (this.mesh.geometry?.dispose) this.mesh.geometry.dispose();
    if (this.backgroundMesh.geometry?.dispose) this.backgroundMesh.geometry.dispose();
    disposeMaterial(this.containerMaterial);
    disposeMaterial(this.backgroundMaterial);
  }
}

function createWorldLabelRenderer({ content, style }) {
  switch (getWorldLabelRendererType(content)) {
    case WORLD_LABEL_RENDERER_SDF:
      return new SdfWorldLabelRenderer({ content, style });
    case WORLD_LABEL_RENDERER_CANVAS:
    default:
      return new CanvasWorldLabelRenderer({ content, style });
  }
}

export {
  CanvasWorldLabelRenderer,
  DEFAULT_SDF_FONT_URL,
  DEFAULT_WORLD_LABEL_RENDERER,
  WORLD_LABEL_SDF_GLYPH_SIZE,
  SdfWorldLabelRenderer,
  WORLD_LABEL_RENDERER_CANVAS,
  WORLD_LABEL_RENDERER_SDF,
  canUseSdfWorldLabelRenderer,
  createWorldLabelRenderer,
  getRequestedWorldLabelRendererType,
  getWorldLabelRendererType,
};
